<script setup lang="ts">
  import { computed, watch } from 'vue';
  import type { ProductFlexibleResource } from '@storefront/resources';
  import { usePersistentFormContext, useTranslator } from '@othercommerce/foundation';
  import ShopProductViewConfiguratorItem from './ShopProductViewConfiguratorItem.vue';

  interface SelectedItem {
    productId: number,
    variantId: number | null,
    isDropShipping: boolean,
    stockAvailable: number,
    stockOnOrder: boolean,
    quantity: number,
  }

  interface Props {
    product: ProductFlexibleResource,
  }

  const required = defineModel('required', { type: Number, required: true, default: 0 });
  const props = defineProps<Props>();
  const translator = useTranslator();
  const { data } = usePersistentFormContext();

  // Compute the current quantity of the items added to the flexible set.
  // We want to determine an integer value, so we divide the quantity by
  // the base unit of the stock.
  const currentQuantity = computed(() => {
    return data.value.items.reduce((accumulator: number, item: SelectedItem) => {
      let flexibleItemDefinition = props.product.flexibleItems.find((definition) => definition.productId === item.productId);

      if (!flexibleItemDefinition) {
        return accumulator;
      }

      return accumulator + (item.quantity / flexibleItemDefinition.stockUnitBase);
    }, 0);
  });

  const requiredQuantity = computed(() => {
    return props.product.minimumItemsQuantity - currentQuantity.value;
  });

  const minimumQuantityMessage = computed(() => {
    let required = requiredQuantity.value;

    if (required > 0) {
      return translator.choice('SHOP.PRODUCT.MESSAGE.SET_MISSING_ITEMS', required, { amount: required });
    }

    return translator.trans('SHOP.PRODUCT.MESSAGE.PROMOTION_CONDITION_MET');
  });

  function addItem() {
    data.value.items.push({
      productId: props.product.flexibleItems[0]!.productId,
      variantId: props.product.flexibleItems[0]!.variantId,
      quantity: +props.product.flexibleItems[0]!.stockUnitBase,
      stockAvailable: props.product.flexibleItems[0]!.stockAvailable,
      stockOnOrder: props.product.flexibleItems[0]!.stockOnOrder,
      isDropShipping: props.product.flexibleItems[0]!.isDropShipping,
      isMedicinalProduct: props.product.flexibleItems[0]!.isMedicinalProduct,
    });
  }

  watch(requiredQuantity, (quantity) => {
    required.value = quantity;
  }, {
    immediate: true,
  });
</script>
<template>
  <div class="flexible-items">
    <div class="flexible-items-content">
      <h2 class="heading">
        {{ $t('SHOP.PRODUCT.HEADER.SET_CONFIGURE') }}:
      </h2>
      <div class="items-grid">
        <ShopProductViewConfiguratorItem v-for="(item, index) in data.items" :selected="item" :product="product" :index="index as number" />
      </div>
      <div class="actions">
        <SfButton small @click="addItem">
          <template #before>
            <SfIconPlus aria-hidden="true" />
          </template>
          {{ $t('SHOP.PRODUCT.ACTION.ADD_PRODUCT_TO_SET') }}
        </SfButton>
      </div>
    </div>
    <p class="minimum-required-quantity" v-html="minimumQuantityMessage" :class="{'danger':requiredQuantity > 0}" />
  </div>
</template>
<style scoped lang="scss">
  .flexible-items {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    .flexible-items-content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .heading {
      font-size: 1rem;
      font-weight: var(--font-weight-bold);
    }

    .items-grid {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .minimum-required-quantity {
      padding: 0.375rem 1rem;
      width: auto;
      font-weight: var(--font-weight-semi);
      text-align: center;
      align-self: center;
      text-wrap: balance;

      color: var(--sf-status-color-success);
      background-color: var(--sf-status-background-success);
      border: 1px solid var(--sf-status-color-success);
      border-radius: var(--border-radius);

      &.danger {
        color: var(--sf-status-color-warning);
        background-color: var(--sf-status-background-warning);
        border: 1px solid var(--sf-status-color-warning);
      }

      :deep(b) {
        font-weight: var(--font-weight-bold);
        color: var(--danger-warning);
      }
    }
  }
</style>
